// src/pages/Hmo/AgentManagement.jsx (or src/pages/AgentManagement.jsx)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { authenticatedFetch, clearStoredCredentials } from '../../utils/api';
import Sidebar from '../../components/Global/sidebar'; // ✅ Import Reusable Sidebar

// PrimeReact Components
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { FilterMatchMode, FilterOperator } from 'primereact/api';

// 🎨 Orange Theme Colors
const BRAND_ORANGE = '#F89423';
const BRAND_ORANGE_DARK = '#ea7c1f';
const BRAND_ORANGE_LIGHT = '#fef3e2';

// ✅ API_BASE Configuration
const API_BASE = (process.env.REACT_APP_API_URL || 'https://localhost:7018').trim();

// ✅ Agent Types Options (fallback with integer IDs)
const AGENT_TYPE_OPTIONS = [
    { label: 'Direct Agent', value: 1 },
    { label: 'Broker', value: 2 },
    { label: 'Corporate Agent', value: 3 },
    { label: 'Referral Partner', value: 4 }
];

// ✅ Status Options
const STATUS_OPTIONS = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' }
];

// ✅ Entity Configuration for Agents
const AGENT_CONFIG = {
    title: 'Agents',
    apiEndpoint: `${API_BASE}/Settings/agentsbycomp`,
    createEndpoint: `${API_BASE}/Settings/create-agent`,
    updateEndpoint: `${API_BASE}/Settings/update-agent`,
    deleteEndpoint: `${API_BASE}/Settings/delete-agent`,
    agentTypesEndpoint: `${API_BASE}/Settings/agent-types`,
    columns: [
        { field: 'id', header: 'ID', sortable: true, style: { width: '80px' } },
        { field: 'agentId', header: 'Agent ID', sortable: true, style: { width: '120px' } },
        { field: 'fullName', header: 'Full Name', sortable: true },
        { field: 'email', header: 'Email', sortable: true },
        { field: 'phoneNumber', header: 'Phone', sortable: true, style: { width: '140px' } },
        { field: 'agentType', header: 'Type', sortable: true, style: { width: '140px' } }
    ],
    formFields: [
        { name: 'fullName', label: 'Full Name', type: 'text', required: true, placeholder: 'e.g., John Doe', maxLength: 100 },
        { name: 'email', label: 'Email', type: 'email', required: true, placeholder: 'e.g., john@example.com' },
        { name: 'phoneNumber', label: 'Phone Number', type: 'text', required: true, placeholder: 'e.g., +234 801 234 5678', maxLength: 20 },
        { name: 'agentTypeId', label: 'Agent Type', type: 'select', required: true, options: AGENT_TYPE_OPTIONS }
    ],
    icon: 'fa-user-tie',
    hasDescription: false,
    hasCategory: false,
    hasStatus: true
};

// ✅ FIXED: Get CompanyId from session - Parse to INTEGER and validate
const getCompanyId = () => {
    try {
        const session = JSON.parse(localStorage.getItem('userSession') || '{}');
        const rawId = session.companyId || session.sessionId;
        
        const parsedId = parseInt(rawId, 10);
        
        if (isNaN(parsedId) || parsedId <= 0) {
            console.warn('⚠️ Invalid companyId in session:', rawId);
            return null;
        }
        
        return parsedId;
    } catch (error) {
        console.error('❌ Error reading session:', error);
        return null;
    }
};

function AgentManagement() {
    const navigate = useNavigate();
    const dt = useRef(null);

    // Sidebar State
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Loading & Data State
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    
    // ✅ Advanced Filter State
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        fullName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
        email: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        phoneNumber: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        agentType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
        agentId: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
        status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] }
    });
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    // Agent Types State
    const [agentTypes, setAgentTypes] = useState(AGENT_TYPE_OPTIONS);
    const [agentTypesLoading, setAgentTypesLoading] = useState(true);

    // Modal State
    const [modalVisible, setModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({});
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const deleteRef = useRef(null);

    // ✅ Load Agent Types from API
    const loadAgentTypes = useCallback(async () => {
        try {
            setAgentTypesLoading(true);
            const response = await authenticatedFetch(AGENT_CONFIG.agentTypesEndpoint, 'POST', {});
            if (response?.status === 'success') {
                const apiData = response.data || [];
                const types = apiData.map(t => ({
                    label: t.name,
                    value: parseInt(t.id, 10)
                })).filter(t => !isNaN(t.value) && t.value > 0);
                
                if (types.length > 0) {
                    setAgentTypes(types);
                } else {
                    setAgentTypes(AGENT_TYPE_OPTIONS);
                }
            } else {
                setAgentTypes(AGENT_TYPE_OPTIONS);
            }
        } catch (error) {
            console.error('Failed to load agent types:', error);
            setAgentTypes(AGENT_TYPE_OPTIONS);
        } finally {
            setAgentTypesLoading(false);
        }
    }, []);

    // ✅ Load Agents Data from API - Filtered by CompanyId
    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            
            const companyId = getCompanyId();
            
            if (!companyId) {
                toast.warning('Session expired. Please log in again.');
                localStorage.removeItem('userSession');
                navigate('/login', { replace: true });
                return;
            }
            
            const payload = { companyId };
            
            console.log('📤 Fetching agents with payload:', payload);
            
            const response = await authenticatedFetch(AGENT_CONFIG.apiEndpoint, 'POST', payload);
            
            if (response?.status === 'success') {
                const apiData = response.data || response.result || [];
                const normalizedData = apiData.map(item => ({
                    id: parseInt(item.id || item.Id, 10),
                    agentId: item.agentId || item.AgentId,
                    fullName: item.fullName || item.FullName,
                    email: item.email || item.Email,
                    phoneNumber: item.phoneNumber || item.PhoneNumber,
                    agentType: item.agentType || item.AgentType,
                    agentTypeId: item.agentTypeId ?? item.AgentTypeId,
                    companyId: parseInt(item.companyId || item.CompanyId, 10),
                    status: item.isActive !== false ? 'Active' : 'Inactive'
                })).filter(item => item.id && item.id > 0);
                
                setData(normalizedData);
                console.log('✅ Agents loaded:', normalizedData.length);
            } else if (response?.status === 'fail' || response?.status === 'error') {
                const msg = (response?.message || '').toLowerCase();
                if (msg.includes('expired') || msg.includes('unauthorized') || msg.includes('401')) {
                    toast.warning('Session expired. Please log in again.');
                    localStorage.removeItem('userSession');
                    navigate('/login', { replace: true });
                    return;
                }
                if (response?.errors) {
                    const errorMessages = Object.values(response.errors).flat().join(', ');
                    throw new Error(errorMessages || response.message || 'Validation failed');
                }
                throw new Error(response.message || 'API request failed');
            } else {
                throw new Error('Invalid API response');
            }
        } catch (error) {
            console.error('💥 Failed to load agents:', error);
            const errorMsg = error.message?.toLowerCase() || '';
            if (errorMsg.includes('401') || errorMsg.includes('expired') || errorMsg.includes('unauthorized')) {
                toast.warning('Session expired. Please log in again.');
                localStorage.removeItem('userSession');
                navigate('/login', { replace: true });
            } else if (errorMsg.includes('invalid companyid')) {
                toast.error('Invalid session. Please log in again.');
                localStorage.removeItem('userSession');
                navigate('/login', { replace: true });
            } else {
                toast.error(error.message || 'Failed to load agents');
            }
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        AOS.init({ duration: 1000, once: true });
        loadAgentTypes();
        loadData();
    }, [loadAgentTypes, loadData]);

    // ✅ Open Create Modal
    const handleCreate = () => {
        setEditingItem(null);
        setFormData({ status: 'Active' });
        setFormErrors({});
        setModalVisible(true);
    };

    // ✅ Open Edit Modal
    const handleEdit = (item) => {
        setEditingItem(item);
        const agentTypeOption = agentTypes.find(t => t.label === item.agentType);
        setFormData({ 
            ...item,
            agentTypeId: agentTypeOption?.value || item.agentTypeId || null
        });
        setFormErrors({});
        setModalVisible(true);
    };

    // ✅ Close Modal
    const handleCloseModal = () => {
        setModalVisible(false);
        setEditingItem(null);
        setFormErrors({});
    };

    // ✅ Delete Agent
    const handleDelete = (event, item) => {
        confirmPopup({
            target: event.currentTarget,
            message: `Are you sure you want to delete "${item.fullName}"?`,
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const deletePayload = { id: parseInt(item.id, 10) };
                    const response = await authenticatedFetch(
                        AGENT_CONFIG.deleteEndpoint,
                        'POST',
                        deletePayload
                    );
                    if (response?.status === 'success') {
                        setData(prev => prev.filter(d => d.id !== item.id));
                        toast.success('Agent deleted successfully');
                    } else {
                        throw new Error(response?.message || 'Delete failed');
                    }
                } catch (error) {
                    console.error('Delete error:', error);
                    toast.error(error.message || 'Delete failed');
                }
            },
            reject: () => {
                toast.info('Delete cancelled');
            }
        });
    };

    // ✅ Handle Form Input Change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // ✅ Validate Form
    const validateForm = () => {
        const errors = {};
        
        AGENT_CONFIG.formFields.forEach(field => {
            const value = formData[field.name];
            if (field.required) {
                if (typeof value === 'string') {
                    if (!value.trim()) errors[field.name] = `${field.label} is required`;
                } else if (value === null || value === undefined || value === '') {
                    errors[field.name] = `${field.label} is required`;
                }
            }
        });
        
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Invalid email format';
        }
        
        if (formData.phoneNumber && formData.phoneNumber.replace(/[\s\-\(\)]/g, '').length < 10) {
            errors.phoneNumber = 'Invalid phone number';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // ✅ Submit Form
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            toast.error('Please fix the errors');
            return;
        }
        try {
            setSubmitting(true);
            
            const companyId = getCompanyId();
            
            if (!companyId || typeof companyId !== 'number' || companyId <= 0) {
                toast.error('Session expired. Please log in again.');
                localStorage.removeItem('userSession');
                navigate('/login', { replace: true });
                return;
            }
            
            const payload = {
                id: editingItem?.id ? parseInt(editingItem.id, 10) : undefined,
                agentId: formData.agentId,
                fullName: formData.fullName?.trim(),
                email: formData.email?.trim(),
                phoneNumber: formData.phoneNumber?.trim(),
                agentTypeId: formData.agentTypeId ? parseInt(formData.agentTypeId, 10) : null,
                companyId: companyId
            };
            
            Object.keys(payload).forEach(key => {
                if (payload[key] === undefined) delete payload[key];
            });
            
            console.log('📤 Submitting payload:', JSON.stringify(payload, null, 2));
            
            const endpoint = editingItem ? AGENT_CONFIG.updateEndpoint : AGENT_CONFIG.createEndpoint;
            const response = await authenticatedFetch(endpoint, 'POST', payload);
            
            if (response?.status === 'success') {
                if (editingItem) {
                    setData(prev => prev.map(item =>
                        item.id === editingItem.id ? { ...item, ...formData } : item
                    ));
                    toast.success('Agent updated successfully');
                } else {
                    toast.success('Agent created successfully');
                    await loadData();
                }
                handleCloseModal();
            } else {
                throw new Error(response?.message || 'Request failed');
            }
        } catch (error) {
            console.error('Submit error:', error);
            toast.error(error.message || 'Operation failed');
        } finally {
            setSubmitting(false);
        }
    };

    // ✅ Handle Logout
    const handleLogout = (e) => {
        e?.preventDefault();
        clearStoredCredentials();
        localStorage.removeItem('userSession');
        toast.success('Logged out successfully');
        navigate('/login', { replace: true });
    };

    // ✅ Navigation Handler
    const handleNavClick = (path, e) => {
        e?.preventDefault();
        if (path) navigate(path);
        if (window.innerWidth < 992) setSidebarOpen(false);
    };

    // ✅ Advanced Filter Handlers
    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const clearFilter = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            fullName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            email: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            phoneNumber: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            agentType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
            agentId: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] }
        });
        setGlobalFilterValue('');
    };

    // ✅ Export Functions
    const exportCSV = () => dt.current?.exportCSV();

    const exportExcel = () => {
        import('xlsx').then((xlsx) => {
            const worksheet = xlsx.utils.json_to_sheet(data);
            const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
            const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
            saveAsExcelFile(excelBuffer, 'agents');
        });
    };

    const saveAsExcelFile = (buffer, fileName) => {
        import('file-saver').then((module) => {
            if (module?.default) {
                const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
                const data = new Blob([buffer], { type: EXCEL_TYPE });
                module.default.saveAs(data, `${fileName}_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`);
            }
        });
    };

    const exportPdf = () => {
        import('jspdf').then((jsPDF) => {
            import('jspdf-autotable').then(() => {
                const doc = new jsPDF.default(0, 0);
                doc.text('Agents Report', 14, 15);
                doc.autoTable({
                    head: [['ID', 'Agent ID', 'Full Name', 'Email', 'Phone', 'Type', 'Status']],
                    body: data.map(item => [
                        item.id, item.agentId, item.fullName, item.email,
                        item.phoneNumber, item.agentType, item.status
                    ])
                });
                doc.save('agents_report.pdf');
            });
        });
    };

    // ✅ Templates
    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2">
            <Button icon="pi pi-pencil" className="p-button-rounded p-button-success p-button-sm"
                onClick={() => handleEdit(rowData)} tooltip="Edit"
                style={{ backgroundColor: BRAND_ORANGE, borderColor: BRAND_ORANGE }} />
            <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-sm"
                onClick={(e) => handleDelete(e, rowData)} tooltip="Delete" ref={deleteRef} />
        </div>
    );

    const statusBodyTemplate = (rowData) => {
        const severity = rowData.status === 'Active' ? 'success' : 'danger';
        return <Tag value={rowData.status || 'N/A'} severity={severity} className="fw-semibold" />;
    };

    const statusFilterTemplate = (options) => (
        <Dropdown value={options.value} options={STATUS_OPTIONS}
            onChange={(e) => options.filterCallback(e.value, options.index)}
            optionLabel="label" optionValue="value" placeholder="Select Status"
            className="p-column-filter" showClear />
    );

    const agentTypeBodyTemplate = (rowData) => (
        <Tag value={rowData.agentType || 'N/A'} severity="info" className="fw-semibold"
            style={{ backgroundColor: BRAND_ORANGE_LIGHT, color: BRAND_ORANGE_DARK, borderColor: BRAND_ORANGE }} />
    );

    const renderHeader = () => (
        <div className="flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="flex align-items-center gap-2">
                <Button type="button" icon="pi pi-filter-slash" label="Clear Filters" outlined
                    onClick={clearFilter} size="small"
                    style={{ borderRight: '1px solid #e5e7eb', paddingRight: '1rem', marginRight: '0.5rem', marginTop: '1rem', borderRadius: '6px 0 0 6px' }} />
                <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange}
                        placeholder="Keyword Search"
                        style={{ width: '250px', borderRadius: '0 6px 6px 0', borderLeft: 'none', marginTop: '1rem' }} />
                </IconField>
            </div>
            <div className="flex align-items-center gap-2 m-2 ml-2">
                <Button type="button" icon="pi pi-file" rounded onClick={() => exportCSV(false)} data-pr-tooltip="CSV" aria-label="Export CSV" />
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" aria-label="Export Excel" />
                <Button type="button" icon="pi pi-file-pdf" severity="warning" rounded onClick={exportPdf} data-pr-tooltip="PDF" aria-label="Export PDF" />
            </div>
        </div>
    );

    // ✅ Loading State
    if (loading && data.length === 0) {
        return (
            <div className="admin-dashboard-page d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
                <div className="text-center">
                    <div className="spinner-border text-orange mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted">Loading Agents...</p>
                </div>
            </div>
        );
    }

    const header = renderHeader();

    return (
        <div className="admin-dashboard-page">
            <ToastContainer position="top-right" autoClose={3000} theme="light" />
            <ConfirmPopup />
            
            {/* ✅ Left Sidebar (Using Reusable Component) */}
            <Sidebar 
                sidebarOpen={sidebarOpen} 
                onNavClick={handleNavClick} 
                onLogout={handleLogout} 
            />
            
            {/* Main Content */}
            <div className="main-content">
                <header className="top-header">
                    <div className="header-left">
                        <button className="sidebar-toggle d-lg-none" onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>
                            <i className="fas fa-bars"></i>
                        </button>
                        <div className="page-title">
                            <h4 className="mb-0" style={{ color: '#1f2937', fontWeight: '600' }}>{AGENT_CONFIG.title}</h4>
                        </div>
                    </div>
                    <div className="header-right">
                        <button className="header-icon"><i className="fas fa-search"></i></button>
                        <button className="header-icon"><i className="fas fa-bell"></i><span className="badge">2</span></button>
                        <div className="user-avatar" style={{ width: '40px', height: '40px', borderRadius: '50%', background: `linear-gradient(135deg, ${BRAND_ORANGE} 0%, ${BRAND_ORANGE_DARK} 100%)`, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', cursor: 'pointer' }}>AD</div>
                    </div>
                </header>
                <div className="content-wrapper" style={{ padding: '30px' }}>
                    {/* Header Section */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                        <div>
                            <h5 className="mb-1">Manage {AGENT_CONFIG.title}</h5>
                            <p className="text-muted small mb-0">{data.length} {AGENT_CONFIG.title.toLowerCase()}</p>
                        </div>
                        <button className="btn" onClick={handleCreate}
                            style={{ backgroundColor: BRAND_ORANGE, color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <i className="fas fa-plus"></i>Add Agent
                        </button>
                    </div>
                    
                    {/* DataTable */}
                    <Card className="border-0 shadow-sm p-5" data-aos="fade-up" data-aos-delay="100">
                        <div className="card-body p-0">
                            <DataTable ref={dt} value={data} paginator showGridlines rows={10}
                                rowsPerPageOptions={[5, 10, 25, 50]} loading={loading} dataKey="id"
                                filters={filters} globalFilterFields={['fullName', 'email', 'phoneNumber', 'agentType', 'agentId', 'status']}
                                header={header} emptyMessage="No agents found." onFilter={(e) => setFilters(e.filters)}
                                tableStyle={{ minWidth: '1000px', marginRight: '1rem', marginTop: '1rem' }}
                                className="p-datatable-sm" responsiveLayout="scroll">
                                <Column field="id" header="ID" sortable style={{ width: '80px' }} filter filterPlaceholder="Search" />
                                <Column field="agentId" header="Agent ID" sortable style={{ width: '120px' }} filter filterPlaceholder="Search ID" />
                                <Column field="fullName" header="Full Name" sortable filter filterPlaceholder="Search by name" style={{ minWidth: '12rem' }} />
                                <Column field="email" header="Email" sortable filter filterPlaceholder="Search by email" style={{ minWidth: '12rem' }} />
                                <Column field="phoneNumber" header="Phone" sortable filter filterPlaceholder="Search phone" style={{ width: '140px' }} />
                                <Column field="agentType" header="Type" sortable style={{ width: '140px' }} body={agentTypeBodyTemplate} filter filterField="agentType" showFilterMatchModes={false} filterMenuStyle={{ width: '14rem' }} filterElement={(options) => (
                                    <Dropdown value={options.value} options={agentTypes}
                                        onChange={(e) => options.filterCallback(e.value, options.index)}
                                        optionLabel="label" optionValue="value" placeholder="Select Type"
                                        className="p-column-filter" showClear />
                                )} />
                                <Column field="status" header="Status" sortable filterMenuStyle={{ width: '14rem' }} style={{ minWidth: '12rem' }} body={statusBodyTemplate} filter filterElement={statusFilterTemplate} />
                                <Column header="Actions" body={actionBodyTemplate} style={{ width: '150px' }} exportable={false} />
                            </DataTable>
                        </div>
                    </Card>
                </div>
            </div>
            
            {/* Modal */}
            {modalVisible && (
                <div className="modal-overlay" onClick={handleCloseModal} style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.3s ease'
                }}>
                    <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{
                        background: 'white', borderRadius: '12px', width: '90%', maxWidth: '600px',
                        maxHeight: '90vh', overflowY: 'visible', position: 'relative',
                        animation: 'slideIn 0.3s ease', zIndex: 2001
                    }}>
                        <div className="modal-header" style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h5 style={{ margin: 0, color: '#1f2937', fontWeight: '600' }}>
                                {editingItem ? 'Edit Agent' : 'Add New Agent'}
                            </h5>
                            <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: 'pointer', padding: '0', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }} onMouseEnter={(e) => e.target.style.background = '#f3f4f6'} onMouseLeave={(e) => e.target.style.background = 'none'}>×</button>
                        </div>
                        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
                            <div style={{ marginBottom: '16px' }}>
                                <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                                <InputText name="fullName" value={formData.fullName || ''} onChange={handleInputChange} className={`w-full ${formErrors.fullName ? 'p-invalid' : ''}`} placeholder="e.g., John Doe" disabled={submitting} style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                                {formErrors.fullName && <small className="p-error" style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{formErrors.fullName}</small>}
                            </div>
                            <div style={{ marginBottom: '16px' }}>
                                <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Email <span style={{ color: '#ef4444' }}>*</span></label>
                                <InputText name="email" type="email" value={formData.email || ''} onChange={handleInputChange} className={`w-full ${formErrors.email ? 'p-invalid' : ''}`} placeholder="e.g., john@example.com" disabled={submitting} style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                                {formErrors.email && <small className="p-error" style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{formErrors.email}</small>}
                            </div>
                            <div style={{ marginBottom: '16px' }}>
                                <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Phone Number <span style={{ color: '#ef4444' }}>*</span></label>
                                <InputText name="phoneNumber" value={formData.phoneNumber || ''} onChange={handleInputChange} className={`w-full ${formErrors.phoneNumber ? 'p-invalid' : ''}`} placeholder="e.g., +234 801 234 5678" disabled={submitting} style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                                {formErrors.phoneNumber && <small className="p-error" style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{formErrors.phoneNumber}</small>}
                            </div>
                            <div style={{ marginBottom: '16px' }}>
                                <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Agent Type <span style={{ color: '#ef4444' }}>*</span></label>
                                <Dropdown name="agentTypeId" value={formData.agentTypeId || null}
                                    onChange={(e) => {
                                        setFormData(prev => ({ ...prev, agentTypeId: e.value }));
                                        if (formErrors.agentTypeId) setFormErrors(prev => ({ ...prev, agentTypeId: '' }));
                                    }}
                                    options={agentTypes} optionLabel="label" optionValue="value"
                                    placeholder="Select agent type" className={`w-full ${formErrors.agentTypeId ? 'p-invalid' : ''}`}
                                    showClear disabled={submitting || agentTypesLoading} style={{ width: '100%' }}
                                    emptyMessage={agentTypesLoading ? "Loading agent types..." : "No agent types available"}
                                    dropdownIcon="pi pi-chevron-down" />
                                {formErrors.agentTypeId && <small className="p-error" style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{formErrors.agentTypeId}</small>}
                            </div>
                            <div style={{ marginBottom: '20px' }}>
                                <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Status</label>
                                <Dropdown name="status" value={formData.status || 'Active'}
                                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.value }))}
                                    options={STATUS_OPTIONS} optionLabel="label" optionValue="value"
                                    disabled={submitting} style={{ width: '100%' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                                <button type="button" onClick={handleCloseModal} disabled={submitting} style={{ padding: '10px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', color: '#374151', cursor: 'pointer', fontWeight: '500', fontSize: '14px' }}>Cancel</button>
                                <button type="submit" disabled={submitting} style={{ padding: '10px 20px', background: submitting ? '#cbd5e1' : BRAND_ORANGE, border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {submitting ? <><i className="fas fa-spinner fa-spin"></i>Saving...</> : <><i className="fas fa-check"></i>{editingItem ? 'Update' : 'Add'} Agent</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            {/* CSS Styles (Sidebar styles removed since they are in Sidebar.css now) */}
            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                ::-webkit-scrollbar { width: 10px; height: 10px; }
                ::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
                ::-webkit-scrollbar-thumb { background: #F89423; border-radius: 10px; border: 2px solid #f1f1f1; }
                ::-webkit-scrollbar-thumb:hover { background: #ea7c1f; }
                * { scrollbar-width: thin; scrollbar-color: #F89423 #f1f1f1; }
                
                .admin-dashboard-page { display: flex; min-height: 100vh; background-color: #f8f9fa; }
                
                .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; }
                .top-header { 
                    background: white; padding: 15px 30px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100;
                }
                .header-left { display: flex; align-items: center; gap: 20px; }
                .header-right { display: flex; align-items: center; gap: 15px; }
                .header-icon { 
                    width: 40px; height: 40px; border-radius: 50%; border: none; background: #f3f4f6;
                    color: #6b7280; cursor: pointer; display: flex; align-items: center; justify-content: center; position: relative; 
                }
                .header-icon:hover { background: #e5e7eb; color: #F89423; }
                .header-icon .badge { 
                    position: absolute; top: 5px; right: 5px; background: #F89423; color: white;
                    font-size: 10px; padding: 2px 5px; border-radius: 10px; 
                }
                
                .card { border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); background: white; border: none; }
                .p-card { border-radius: 12px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important; }
                .p-button { border-radius: 8px !important; font-weight: 500; }
                .p-inputtext { border-radius: 8px !important; border: 1px solid #d1d5db !important; padding: 10px 14px !important; }
                .p-inputtext:focus { border-color: #F89423 !important; box-shadow: 0 0 0 2px rgba(248,148,35,0.2) !important; }
                .p-dropdown { border-radius: 8px !important; }
                .p-invalid { border-color: #ef4444 !important; }
                .p-datatable .p-datatable-thead > tr > th { background: #f9fafb !important; font-weight: 600 !important; font-size: 13px !important; color: #374151 !important; }
                .p-datatable .p-datatable-tbody > tr > td { padding: 12px 16px !important; font-size: 14px !important; }
                .p-datatable .p-paginator { background: #f8f9fa !important; }
                .p-dropdown-panel { z-index: 2100 !important; }
                
                @media (max-width: 992px) { 
                    .main-content { margin-left: 0; } 
                }
                @media (max-width: 768px) { 
                    .content-wrapper { padding: 15px; } 
                    .top-header { padding: 15px; } 
                    .modal-panel { width: 95% !important; } 
                }
            `}</style>
        </div>
    );
}

export default AgentManagement;